import * as React from 'react';
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DoNotStripSpacesSpan from '../common/DoNotStripSpacesSpan';
import DownloadButton from './download/DownloadButton';
import IsStaff from '../common/IsStaff';
import ExtractionBatchReExtract from './actions/ExtractionBatchReExtract';
import useGlynt from '../hooks/useGlynt';
import { AUTH_CONFIG } from '../../auth0-config';
import { AppContext } from '../../App';
import { IExtractionBatch } from '../../types/my-types';
import { Link } from 'react-router';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { useQueryClient } from '@tanstack/react-query';

interface ExtractionBatchRowGroupProps {
  openRow: boolean;
  eb: any;
}

function ExtractionBatchRowGroup({ openRow, eb }: ExtractionBatchRowGroupProps) {
  const { dataPoolId } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();

  const [groups, setGroups] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  // eb in the list view does not contain extractions_by_training_set
  const [ebFull, setEbFull] = React.useState<IExtractionBatch>(eb);

  // TODO: improve performance
  React.useEffect(() => {
    // let extractionGroups: any[] = [];
    // trigger reload extraction batch when expand to view groups
    if (openRow) {
      client
        .fetchQuery({
          queryKey: ['extractionBatch', eb.id],
          queryFn: async () => await (await glynt).extractionBatch.retrieve(eb.id),
        })
        .then((data) => {
          const extractionsByTrainingSet = data.extractions_by_training_set;
          const trainingSetUrls = extractionsByTrainingSet
            ? Object.keys(extractionsByTrainingSet)
            : [];
          if (trainingSetUrls.length > 0) {
            Promise.all(
              trainingSetUrls
                .filter((url) => url !== 'None')
                .map(async (url: string, index: number) => {
                  let trainingSetId = getIdFromUrl(url);
                  return client
                    .fetchQuery({
                      queryKey: ['trainingSet', trainingSetId],
                      queryFn: async () => await (await glynt).trainingSet.retrieve(trainingSetId),
                    })
                    .then((data: any) => {
                      return {
                        groupKey: url,
                        trainingSetId,
                        trainingSetLabel: data.label,
                        extractions: extractionsByTrainingSet[url],
                      };
                    });
                })
            )
              .then((results) => {
                if (Object.keys(extractionsByTrainingSet).includes('None')) {
                  setGroups([
                    ...results,
                    {
                      groupKey: 'Unclassified',
                      trainingSetId: 'None',
                      trainingSetLabel: 'Unclassified',
                      extractions: extractionsByTrainingSet['None'],
                    },
                  ]);
                } else {
                  setGroups(results);
                }
              })
              .catch(console.error);
          }
          setEbFull(data);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, [eb, openRow]);

  if (!openRow) return null;

  // TODO: check core version
  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
        <Collapse in={openRow} timeout="auto" unmountOnExit>
          <Box margin={1} data-cy={`tbl-row-auto-classify-${eb.label}`}>
            <Typography variant="h6" gutterBottom component="div">
              <DoNotStripSpacesSpan
                title={eb.label}
                cypressId={`span-extraction-batch-${eb.label}`}
              />
            </Typography>
            {loading ? (
              <LinearProgress />
            ) : (
              <Table size="small" aria-label="groups">
                <TableHead>
                  <TableRow>
                    <TableCell>Training Set</TableCell>
                    <TableCell># of Docs</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>Export</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group, index) => (
                    <TableRow key={`group-${index}`}>
                      <TableCell>{group.trainingSetLabel}</TableCell>
                      <TableCell>{group.extractions.length}</TableCell>
                      <TableCell>
                        <Link
                          to={
                            group.trainingSetId === 'None'
                              ? `/data-pools/${dataPoolId}/extraction-batch/${eb.id}/details` // TODO: legacy group path; map to details for now
                              : `/data-pools/${dataPoolId}/extraction-batch/${eb.id}/details` // TODO: group view to be revisited
                          }
                          style={{ textDecoration: 'none' }}
                        >
                          <Button color="inherit" style={{ marginRight: 5 }}>
                            Details
                          </Button>
                        </Link>
                        <Link
                          to={`/data-pools/${dataPoolId}/extraction-batch/${eb.id}/review/${getIdFromUrl(
                            group.extractions[0]
                          )}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Button color="inherit">Review</Button>
                        </Link>

                        <IsStaff>
                          <a
                            href={`${AUTH_CONFIG.cleanToolUrl}/glynt-import?datapool_id=${dataPoolId}&extraction_batch_id=${eb.id}&training_set_id=${group.trainingSetId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Button color="inherit">Clean</Button>
                          </a>
                        </IsStaff>

                        <ExtractionBatchReExtract
                          extractionBatch={ebFull}
                          groupKey={group.groupKey}
                          coreVersion="4.0.0"
                          handleClose={() => {}}
                          type="menu"
                        />
                      </TableCell>
                      <TableCell>
                        <DownloadButton
                          extractionBatchId={eb.id}
                          extractionBatchLabel={`${eb.label}-${group.trainingSetLabel}`}
                          failed={eb.status === 'Failed'}
                          trainingSetId={group.trainingSetId}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

export default ExtractionBatchRowGroup;
